import { useState, useEffect } from "react";
import RichTextEditor from "../../../common/richTextEditor";
import { BaseUrl } from "../../../common/config";
import ConfirmationPopup from "../../../common/confirmationPopup";
import { useTranslation } from "react-i18next";
import { canAction } from "../../../common/permissionhelper";
const MODULE = "manage_foodservicepartners";

const WhyPartnerSectionEditor = ({ section, onUpdate, onSave, isSaving }) => {
  const { t } = useTranslation();
  const canEdit = canAction(MODULE, "edit");
  const isReadOnly = !canEdit;
  const [showDeletePopup, setShowDeletePopup] = useState(false);
  const [benefitToDelete, setBenefitToDelete] = useState(null);
  const [formData, setFormData] = useState(
    section || { title: "", description: "", imageUrl: "", benefits: [] },
  );
  // Initialize form data when section prop changes
  useEffect(() => {
    if (section) {
      const updatedBenefits = section.benefits?.map((benefit) => ({
        ...benefit,
        isActive: benefit.isActive !== false, // default true
      }));
      setFormData({
        ...section,
        benefits: updatedBenefits || [],
      });
    }
  }, [section]);
  // Handle input changes
  const handleRichTextChange = (html) => {
    const updatedData = { ...formData, description: html };
    setFormData(updatedData);
    onUpdate && onUpdate(updatedData);
  };
  // Handle image upload
  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const previewUrl = URL.createObjectURL(file);
    const updatedData = {
      ...formData,
      imageFile: file, // send real file to backend
      imageUrl: previewUrl, // preview only
    };
    setFormData(updatedData);
    onUpdate && onUpdate(updatedData);
  };
  // Handle benefits changes
  const handleBenefitChange = (index, field, value) => {
    const updatedBenefits = [...formData.benefits];
    updatedBenefits[index] = { ...updatedBenefits[index], [field]: value };
    const updatedData = { ...formData, benefits: updatedBenefits };
    setFormData(updatedData);
    onUpdate && onUpdate(updatedData);
  };
  // Handle benefit icon upload
  const handleBenefitIconUpload = (index, e) => {
    const file = e.target.files[0];
    if (!file) return;
    const previewUrl = URL.createObjectURL(file);
    const updatedBenefits = [...formData.benefits];
    updatedBenefits[index] = {
      ...updatedBenefits[index],
      iconFile: file, // real file for backend
      iconPreview: previewUrl, // preview only
    };
    const updatedData = { ...formData, benefits: updatedBenefits };
    setFormData(updatedData);
    onUpdate && onUpdate(updatedData);
  };
  // Benifits Accordion
  const [openIndex, setOpenIndex] = useState(null);

  const toggleAccordion = (index) => {
    setOpenIndex((prev) => (prev === index ? null : index));
  };

  document.querySelectorAll(".switch input").forEach((toggle) => {
    toggle.addEventListener("change", function () {
      const card = this.closest(".card");
      if (this.checked) {
        card.classList.add("enabled");
        card.classList.remove("disabled");
      } else {
        card.classList.remove("enabled");
        card.classList.add("disabled");
      }
    });
  });
  // Add benefits
  const addBenefit = () => {
    const updatedData = {
      ...formData,
      benefits: [
        ...formData.benefits,
        { title: "", description: "", iconUrl: "", isActive: true },
      ],
    };
    setFormData(updatedData);
    onUpdate && onUpdate(updatedData);
  };
  // Remove benefit by index
  const removeBenefit = (index) => {
    const updatedData = {
      ...formData,
      benefits: formData.benefits.filter((_, i) => i !== index),
    };
    setFormData(updatedData);
    onUpdate && onUpdate(updatedData);
  };
  // Handle removing the uploaded image
  const handleRemoveImage = () => {
    const updatedData = {
      ...formData,
      imageUrl: "",
      imageFile: null,
    };
    setFormData(updatedData);
    onUpdate && onUpdate(updatedData);
  };
  // Handle removing the uploaded benefit icon
  const handleRemoveBenefitIcon = (index) => {
    const updatedBenefits = [...formData.benefits];
    updatedBenefits[index] = {
      ...updatedBenefits[index],
      iconUrl: "",
      iconFile: null,
      iconPreview: null,
    };
    const updatedData = {
      ...formData,
      benefits: updatedBenefits,
    };
    setFormData(updatedData);
    onUpdate && onUpdate(updatedData);
  };
  // Render form fields
  return (
    <div className="section-editor">
      {/* Section Title */}
      <div className="input-field">
        <label>{t("section_title_txt")}</label>
        <RichTextEditor
          emailContent={formData.title || ""}
          handleChangeEmailContent={(html) => {
            const updatedData = { ...formData, title: html };
            setFormData(updatedData);
            onUpdate && onUpdate(updatedData);
          }}
          readOnly={isReadOnly}
        />
      </div>
      {/* Section Description */}
      <div className="input-field">
        <label>{t("section_description_txt")}</label>
        <RichTextEditor
          emailContent={formData.description || ""}
          handleChangeEmailContent={handleRichTextChange}
          readOnly={isReadOnly}
        />
      </div>
      {/* Section Image Upload */}
      <div className="notification-field-set sectore1-cms-field-set">
        <div className="input-field col-1">
          <label>
            {t("section_image_txt")}{" "}
            <span className="text-note">{t("imgage_only_allowed_txt")}</span>
          </label>
          <div className="flex align-center gap-10">
            {/* Show Upload Input Only If No Image */}
            {!formData.imageUrl && (
              <input
                type="file"
                accept="image/png, image/jpeg, image/jpg"
                onChange={handleImageUpload}
              />
            )}
            {/* Show Preview If Image Exists */}
            {formData.imageUrl && (
              <div className="upld-img-file">
                <div className="upld-img-show">
                  <img
                    src={
                      formData.imageFile
                        ? URL.createObjectURL(formData.imageFile)
                        : `${BaseUrl}${formData.imageUrl}`
                    }
                    alt={t("section_preview_txt")}
                  />
                  {/* Delete Icon */}
                  {!isReadOnly && (
                    <a
                      className="title-tooltip title-up delete-img"
                      data-title={t("delete_txt")}
                      onClick={() => setShowDeletePopup(true)}
                    >
                      <i className="fa fa-trash" aria-hidden="true"></i>
                    </a>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      {/* Benefits Section */}
      <div className="input-field-set">
        <div className="input-field">
          <div className="input-field-hdr">
            <div className="flex align-center justify-space ">
              <label>{t("benefits_txt")}</label>
              {!isReadOnly && (
                <button
                  className="btn-secondary btn-md"
                  type="button"
                  onClick={addBenefit}
                >
                  <svg
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    viewBox="0 0 24 24"
                  >
                    <path d="M12 5v14M5 12h14" />
                  </svg>
                  {t("add_benefit_txt")}
                </button>
              )}
            </div>
          </div>
          <div className="flex-card-row">
            {[...(formData.benefits || [])]
              .slice()
              .reverse()
              .map((benefit, reversedIndex) => {
                const index = formData.benefits.length - 1 - reversedIndex;
                return (
                  <div
                    className={`card ${benefit.isActive !== false ? "enabled" : "disabled"}`}
                    key={index}
                  >
                    {/* Accordion Header */}
                    <div
                      className="accordion-header"
                      onClick={() => toggleAccordion(index)}
                    >
                      <h4>
                        {t("benefits_txt")}{" "}
                        {String(
                          formData.benefits.length - reversedIndex,
                        ).padStart(2, "0")}
                      </h4>
                      <span
                        className={`arrow ${openIndex === index ? "open" : ""}`}
                      ></span>
                    </div>
                    {/* Accordion Body */}
                    <div
                      className={`accordion-body ${
                        openIndex === index ? "open" : ""
                      }`}
                    >
                      {/* SWITCH */}
                      <div className="switch-btn">
                        <label className="switch">
                          <input
                            type="checkbox"
                            checked={benefit.isActive !== false}
                            onChange={(e) => {
                              if (isReadOnly) return;
                              handleBenefitChange(
                                index,
                                "isActive",
                                e.target.checked,
                              );
                            }}
                          />
                          <span className="slider"></span>
                        </label>
                        <span className="switch-label">
                          {benefit.isActive !== false
                            ? t("enabled_txt")
                            : t("disabled_txt")}
                        </span>
                      </div>
                      {/* Icon Upload */}
                      <div className="icon-uploads-field">
                        <label>
                          {t("icon_txt")}{" "}
                          <span className="text-note">
                            {t("imgage_only_allowed_txt")}
                          </span>
                        </label>
                        <div className="flex align-center gap-10">
                          {/* Show Upload Only If No Icon */}
                          {!benefit.iconPreview && !benefit.iconUrl && (
                            <input
                              type="file"
                              accept="image/png, image/jpeg, image/jpg"
                              onChange={(e) =>
                                handleBenefitIconUpload(index, e)
                              }
                            />
                          )}
                          {/* Show Preview If Icon Exists */}
                          {(benefit.iconPreview || benefit.iconUrl) && (
                            <div className="upld-img-file">
                              <div className="upld-img-show">
                                <img
                                  src={
                                    benefit.iconPreview
                                      ? benefit.iconPreview
                                      : benefit.iconUrl
                                        ? `${BaseUrl}${benefit.iconUrl}`
                                        : ""
                                  }
                                  alt={t("icon_preview_txt")}
                                />
                                {/* Delete Icon (same class as other image) */}
                                {!isReadOnly && (
                                  <a
                                    className="title-tooltip title-up delete-img"
                                    data-title={t("delete_txt")}
                                    onClick={() =>
                                      handleRemoveBenefitIcon(index)
                                    }
                                  >
                                    <i
                                      className="fa fa-trash"
                                      aria-hidden="true"
                                    ></i>
                                  </a>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                      {/* Title */}
                      <input
                        type="text"
                        value={benefit.title || ""}
                        onChange={(e) =>
                          handleBenefitChange(index, "title", e.target.value)
                        }
                        placeholder={t("benefit_title_txt")}
                        readOnly={isReadOnly}
                      />
                      {/* Description */}
                      <textarea
                        value={benefit.description || ""}
                        onChange={(e) =>
                          handleBenefitChange(
                            index,
                            "description",
                            e.target.value,
                          )
                        }
                        placeholder={t("benefit_description_txt")}
                        rows="3"
                        readOnly={isReadOnly}
                      />
                      {!isReadOnly && (
                        <button
                          className="btn-md remove-card-btn btn-secondary"
                          type="button"
                          onClick={() => setBenefitToDelete(index)}
                        >
                          <i className="fa fa-trash-o"></i>
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
          </div>
        </div>
      </div>
      {!isReadOnly && (
        <div className="btnfield">
          <button
            title={t("save_section_txt")}
            type="button"
            onClick={() => onSave && onSave("whyPartner", formData)}
            disabled={isSaving}
          >
            {isSaving ? t("saving_txt") : t("save_section_txt")}
          </button>
        </div>
      )}
      {showDeletePopup && (
        <ConfirmationPopup
          title={t("confirm_txt")}
          confirmationMessage={t("confirm_delete_value_proposition_image_txt")}
          handleYes={() => {
            handleRemoveImage();
            setShowDeletePopup(false);
          }}
          handleNo={() => setShowDeletePopup(false)}
        />
      )}
      {benefitToDelete !== null && (
        <ConfirmationPopup
          title={t("confirm_txt")}
          confirmationMessage={t("confirm_delete_benefit_txt")}
          handleYes={() => {
            removeBenefit(benefitToDelete);
            setBenefitToDelete(null);
          }}
          handleNo={() => setBenefitToDelete(null)}
        />
      )}
    </div>
  );
};

export default WhyPartnerSectionEditor;
